import type { Rol } from '@discopico/domain';

/**
 * La matriz de 24 acciones de la feature 2, como TABLA DE DATOS y no como condicionales
 * dispersos.
 *
 * ADVERTENCIA QUE HAY QUE LEER ANTES DE USAR ESTO: el chequeo de permisos en el cliente
 * sirve para ESCONDER CONTROLES, nunca como seguridad. La seguridad son las políticas y
 * las funciones de Postgres. Quien venga de un proyecto sin seguridad por filas va a
 * asumir que este objeto es el control, y no lo es: la llave pública está en el bundle
 * y cualquiera puede llamar la API directo.
 */
export const PERMISOS = {
  'jornada.abrirCerrar':      ['operario', 'jefe_operarios', 'administrador'],
  'ingreso.registrar':        ['operario', 'jefe_operarios', 'administrador'],
  'conteo.registrar':         ['operario', 'jefe_operarios', 'administrador'],
  'gastoCajaMenor.registrar': ['operario', 'jefe_operarios', 'contador', 'administrador'],
  'compra.registrar':         ['contador', 'administrador'],
  'inventario.contar':        ['operario', 'jefe_operarios', 'administrador'],
  'compra.validar':           ['administrador'],
  'movimiento.anular':        ['jefe_operarios', 'contador', 'administrador'],
  'jornada.reabrir':          ['jefe_operarios', 'administrador'],
  'faltante.asignar':         ['jefe_operarios', 'administrador'],
  'transferencia.registrar':  ['contador', 'administrador'],
  'proveedor.administrar':    ['contador', 'administrador'],
  'nomina.liquidar':          ['contador', 'administrador'],
  'retencion.registrar':      ['contador', 'administrador'],
  'banco.conciliar':          ['contador', 'administrador'],
  'periodo.cerrar':           ['contador', 'administrador'],
  'periodo.reabrir':          ['administrador'],
  'utilidad.ver':             ['contador', 'administrador'],
  'alertas.ver':              ['jefe_operarios', 'contador', 'administrador'],
  'consolidado.exportar':     ['contador', 'administrador'],
  'cajaMenor.ajustarBase':    ['administrador'],
  'socios.registrar':         ['administrador'],
  'categoria.administrar':    ['contador', 'administrador'],
  'usuario.administrar':      ['administrador'],
} as const satisfies Record<string, readonly Rol[]>;

export type Accion = keyof typeof PERMISOS;

/**
 * Responde si el rol puede hacer la acción, según el espejo del frontend.
 *
 * Acepta `null` y `undefined` porque el estado de sesión del cliente los produce
 * mientras el token se está leyendo, y un componente que renderiza antes de tener
 * el rol tiene que esconder los controles, no reventar.
 *
 * Niega por omisión ante una acción desconocida. La versión de la fase 0 hacía
 * `PERMISOS[accion].includes(rol)`, que con una acción que no existe lanza
 * `Cannot read properties of undefined`, y una pantalla en blanco es peor que un
 * botón escondido.
 */
export function puede(rol: Rol | null | undefined, accion: Accion): boolean {
  if (!rol) return false;
  const fila = PERMISOS[accion] as readonly Rol[] | undefined;
  if (!fila) return false;
  return fila.includes(rol);
}

/**
 * Las 24 acciones, en el orden en que están declaradas en `PERMISOS`, que es el de
 * la tabla de la feature 2. Sirve para recorrerlas en una prueba o en un menú.
 */
export const ACCIONES = Object.keys(PERMISOS) as readonly Accion[];

/**
 * Etiquetas en español para los mensajes y los menús de la interfaz.
 *
 * Van en un objeto aparte de `PERMISOS` a propósito: así agregar o corregir un texto
 * nunca puede alterar un permiso por accidente, porque son dos archivos de datos
 * distintos y el diff de uno no toca al otro.
 */
export const ETIQUETA_ACCION: Record<Accion, string> = {
  'jornada.abrirCerrar': 'abrir y cerrar la jornada',
  'ingreso.registrar': 'registrar ventas, cover y propinas',
  'conteo.registrar': 'registrar el conteo de efectivo',
  'gastoCajaMenor.registrar': 'registrar gastos de caja menor',
  'compra.registrar': 'registrar una compra de inventario',
  'inventario.contar': 'registrar el conteo físico de inventario',
  'compra.validar': 'validar compras',
  'movimiento.anular': 'anular un movimiento',
  'jornada.reabrir': 'reabrir una jornada cerrada',
  'faltante.asignar': 'asignar un faltante a un operario',
  'transferencia.registrar': 'registrar pagos por transferencia',
  'proveedor.administrar': 'administrar el directorio de proveedores',
  'nomina.liquidar': 'liquidar y pagar nómina',
  'retencion.registrar': 'registrar retenciones y pagar impuestos',
  'banco.conciliar': 'conciliar la cuenta bancaria',
  'periodo.cerrar': 'cerrar un periodo',
  'periodo.reabrir': 'reabrir un periodo',
  'utilidad.ver': 'ver la utilidad y los márgenes',
  'alertas.ver': 'ver el tablero de alertas',
  'consolidado.exportar': 'exportar el consolidado',
  'cajaMenor.ajustarBase': 'ajustar el valor base de caja menor',
  'socios.registrar': 'registrar aportes y retiros de socios',
  'categoria.administrar': 'administrar categorías y presupuesto',
  'usuario.administrar': 'crear y desbloquear usuarios',
};

/** Las acciones que el rol puede hacer. Sirve para armar menús. */
export function accionesDe(rol: Rol | null | undefined): Accion[] {
  if (!rol) return [];
  return ACCIONES.filter((a) => puede(rol, a));
}
